import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import afterSaleServiceApi from '../../api/afterSaleService';

// 格式化时间数组为字符串
const formatCreateTime = (timeArray) => {
  if (!timeArray || !Array.isArray(timeArray) || timeArray.length < 6) return '-';
  
  const [year, month, day, hours, minutes, seconds] = timeArray;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ` +
         `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// 分页查询售后服务
export const fetchAfterSaleServicePage = createAsyncThunk(
  'afterSaleService/fetchPage',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await afterSaleServiceApi.pageAfterSaleServices(params);
      
      // 处理响应数据
      const processedData = {
        records: (response.data && response.data.records ? response.data.records : []).map(item => {
          const afterSaleService = item.afterSaleService || item;
          
          return {
            ...afterSaleService,
            // 转换时间格式
            createTime: formatCreateTime(afterSaleService.createTime),
            updateTime: formatCreateTime(afterSaleService.updateTime),
            auditTime: formatCreateTime(afterSaleService.auditTime),
            handleTime: formatCreateTime(afterSaleService.handleTime),

            // 服务类型映射
            serviceTypeText: {
              0: '退款',
              1: '退货',
              2: '换货'
            }[afterSaleService.serviceTypeId] || '未知',

            // 退款方式映射
            refundMethodText: {
              1: '原路退款',
              2: '线下退款'
            }[afterSaleService.refundMethod] || '未知',

            // 状态映射
            statusText: {
              'PENDING_REVIEW': '待审核',
              'APPROVED': '审核通过',
              'PROCESSING': '处理中',
              'COMPLETED': '已完成',
              'REJECTED': '已拒绝',
              'CANCELLED': '已取消'
            }[afterSaleService.status] || afterSaleService.status,

            // 额外的订单信息
            storeName: item.storeName || '-',
            orderItems: item.orderItems || []
          };
        }),
        total: response.data?.total || 0,
        current: response.data?.current || params.page || 1,
        size: response.data?.size || params.pageSize || 10,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取售后服务列表失败' });
    }
  }
);

// 创建售后服务
export const createAfterSaleService = createAsyncThunk(
  'afterSaleService/create',
  async (afterSaleService, { rejectWithValue }) => {
    try {
      const response = await afterSaleServiceApi.createAfterSaleService(afterSaleService);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '创建售后服务失败' });
    }
  }
);

// 获取售后服务详情
export const fetchAfterSaleServiceDetail = createAsyncThunk(
  'afterSaleService/fetchDetail',
  async (afterSaleServiceId, { rejectWithValue }) => {
    try {
      const response = await afterSaleServiceApi.getAfterSaleServiceDetail(afterSaleServiceId);
      
      // 处理详情数据
      const processedDetail = {
        ...response.data,
        createTime: formatCreateTime(response.data.createTime),
        updateTime: formatCreateTime(response.data.updateTime),
        auditTime: formatCreateTime(response.data.auditTime),
        handleTime: formatCreateTime(response.data.handleTime)
      };

      return processedDetail;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取售后服务详情失败' });
    }
  }
);

// 更新售后服务
export const updateAfterSaleService = createAsyncThunk(
  'afterSaleService/update',
  async ({ afterSaleServiceId, afterSaleService }, { rejectWithValue }) => {
    try {
      const response = await afterSaleServiceApi.updateAfterSaleService(afterSaleServiceId, afterSaleService);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新售后服务失败' });
    }
  }
);

// 删除售后服务
export const deleteAfterSaleService = createAsyncThunk(
  'afterSaleService/delete',
  async (afterSaleServiceId, { rejectWithValue }) => {
    try {
      const response = await afterSaleServiceApi.deleteAfterSaleService(afterSaleServiceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除售后服务失败' });
    }
  }
);

// 审核售后退款申请
export const auditAfterSaleRefund = createAsyncThunk(
  'afterSaleService/auditRefund',
  async ({ afterSaleServiceId, auditRequest }, { rejectWithValue, dispatch }) => {
    try {
      const response = await afterSaleServiceApi.auditAfterSaleRefund(afterSaleServiceId, auditRequest);
      if (!response || response.code !== 200) {
        const errorMsg = response?.message || '审核售后退款申请失败';
        message.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      message.success('审核成功');
      // 重新获取列表
      dispatch(fetchAfterSaleServicePage({ page: 1, pageSize: 10 }));
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || '审核售后退款申请失败';
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

const afterSaleServiceSlice = createSlice({
  name: 'afterSaleService',
  initialState: {
    afterSaleServices: [],
    afterSaleServiceDetail: null,
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    // 获取售后服务列表
    builder.addCase(fetchAfterSaleServicePage.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAfterSaleServicePage.fulfilled, (state, action) => {
      state.loading = false;
      state.afterSaleServices = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchAfterSaleServicePage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取售后服务详情
    builder.addCase(fetchAfterSaleServiceDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAfterSaleServiceDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.afterSaleServiceDetail = action.payload;
    });
    builder.addCase(fetchAfterSaleServiceDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建售后服务
    builder.addCase(createAfterSaleService.fulfilled, (state) => {
      // 可以在这里添加创建成功后的逻辑
    });

    // 更新售后服务
    builder.addCase(updateAfterSaleService.fulfilled, (state) => {
      // 可以在这里添加更新成功后的逻辑
    });

    // 删除售后服务
    builder.addCase(deleteAfterSaleService.fulfilled, (state) => {
      // 可以在这里添加删除成功后的逻辑
    });

    // 审核售后退款申请
    builder.addCase(auditAfterSaleRefund.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(auditAfterSaleRefund.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(auditAfterSaleRefund.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export default afterSaleServiceSlice.reducer; 